<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { format, parseISO } from 'date-fns';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const isLoading = ref(true);
const groups = ref([]);
// Mantém o id do contato escolhido como "base" por grupo.
const selectedBaseIds = ref({});
const mergingGroupIndex = ref(null);
const confirmDialogRef = ref(null);
const pendingGroupIndex = ref(null);

const groupKey = group => `${group.field}:${group.value}`;

// Default: maior nº de conversas; empate -> mais antigo (created_at).
const pickDefaultBaseId = group => {
  const sorted = [...group.contacts].sort((a, b) => {
    if (b.conversations_count !== a.conversations_count) {
      return b.conversations_count - a.conversations_count;
    }
    return new Date(a.created_at) - new Date(b.created_at);
  });
  return sorted[0]?.id;
};

const formatCreatedAt = isoString => {
  if (!isoString) return '';
  try {
    return format(parseISO(isoString), 'MMM d, yyyy');
  } catch {
    return '';
  }
};

const fieldBadgeLabel = field =>
  field === 'phone_number'
    ? t('CONTACTS_LAYOUT.DUPLICATES.BADGE.PHONE')
    : t('CONTACTS_LAYOUT.DUPLICATES.BADGE.NAME');

const fetchDuplicates = async () => {
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.getDuplicates();
    groups.value = data.payload ?? [];
    const defaults = {};
    groups.value.forEach(group => {
      defaults[groupKey(group)] = pickDefaultBaseId(group);
    });
    selectedBaseIds.value = defaults;
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DUPLICATES.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const goBack = () => {
  router.push({
    name: 'contacts_dashboard_index',
    params: { accountId: route.params.accountId },
    query: { page: 1 },
  });
};

const openMergeConfirm = index => {
  pendingGroupIndex.value = index;
  confirmDialogRef.value?.open?.();
};

const mergeGroup = async () => {
  const index = pendingGroupIndex.value;
  if (index === null) return;

  const group = groups.value[index];
  const baseId = selectedBaseIds.value[groupKey(group)];
  const mergeeIds = group.contacts
    .map(contact => contact.id)
    .filter(id => id !== baseId);

  confirmDialogRef.value?.close?.();
  mergingGroupIndex.value = index;

  try {
    // 3+ contatos: mescla cada não-mantido no base, um de cada vez.
    // eslint-disable-next-line no-restricted-syntax
    for (const mergeeId of mergeeIds) {
      // eslint-disable-next-line no-await-in-loop
      await store.dispatch('contacts/merge', {
        parentId: baseId,
        childId: mergeeId,
      });
    }
    groups.value = groups.value.filter((_, i) => i !== index);
    useAlert(t('CONTACTS_LAYOUT.DUPLICATES.MERGE_SUCCESS'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DUPLICATES.MERGE_ERROR'));
    // Re-sincroniza: num grupo 3+, um merge pode ter ocorrido antes da falha,
    // deixando a lista local stale.
    await fetchDuplicates();
  } finally {
    mergingGroupIndex.value = null;
    pendingGroupIndex.value = null;
  }
};

onMounted(fetchDuplicates);
</script>

<template>
  <div class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-background">
    <header
      class="flex items-center gap-3 w-full py-6 px-6 mx-auto max-w-[60rem]"
    >
      <Button
        icon="i-lucide-arrow-left"
        color="slate"
        variant="ghost"
        size="sm"
        @click="goBack"
      />
      <div class="flex flex-col">
        <span class="text-xl font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.DUPLICATES.TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.DUPLICATES.DESCRIPTION') }}
        </span>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] px-6 pb-10">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div
          v-else-if="!groups.length"
          class="flex items-center justify-center py-16"
        >
          <span class="text-base text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DUPLICATES.EMPTY') }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-6">
          <div
            v-for="(group, index) in groups"
            :key="groupKey(group)"
            class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-2 min-w-0">
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-n-alpha-2 text-n-slate-11"
                >
                  <Icon
                    :icon="
                      group.field === 'phone_number'
                        ? 'i-lucide-phone'
                        : 'i-lucide-user'
                    "
                    class="size-3"
                  />
                  {{ fieldBadgeLabel(group.field) }}
                </span>
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ group.value }}
                </span>
              </div>
              <Button
                :label="t('CONTACTS_LAYOUT.DUPLICATES.MERGE_BUTTON')"
                color="blue"
                size="sm"
                :is-loading="mergingGroupIndex === index"
                :disabled="mergingGroupIndex !== null"
                @click="openMergeConfirm(index)"
              />
            </div>

            <ul class="flex flex-col gap-2">
              <li
                v-for="contact in group.contacts"
                :key="contact.id"
                class="flex items-start gap-3 p-3 rounded-lg border border-n-weak"
                :class="
                  selectedBaseIds[groupKey(group)] === contact.id
                    ? 'bg-n-alpha-2 border-n-brand'
                    : ''
                "
              >
                <label
                  class="flex items-center pt-0.5 cursor-pointer shrink-0"
                  :title="t('CONTACTS_LAYOUT.DUPLICATES.KEEP_LABEL')"
                >
                  <input
                    v-model="selectedBaseIds[groupKey(group)]"
                    type="radio"
                    :name="groupKey(group)"
                    :value="contact.id"
                    class="accent-n-brand size-4"
                  />
                </label>
                <div class="flex flex-col gap-1 min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium truncate text-n-slate-12">
                      {{
                        contact.name || t('CONTACTS_LAYOUT.DUPLICATES.NO_NAME')
                      }}
                    </span>
                    <span
                      v-if="selectedBaseIds[groupKey(group)] === contact.id"
                      class="text-xs font-medium text-n-brand"
                    >
                      {{ t('CONTACTS_LAYOUT.DUPLICATES.KEEP_LABEL') }}
                    </span>
                  </div>
                  <div
                    class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-n-slate-11"
                  >
                    <span v-if="contact.phone_number">
                      {{ contact.phone_number }}
                    </span>
                    <span v-if="contact.email">{{ contact.email }}</span>
                    <span>
                      {{
                        t('CONTACTS_LAYOUT.DUPLICATES.CONVERSATIONS_COUNT', {
                          count: contact.conversations_count,
                        })
                      }}
                    </span>
                    <span v-if="contact.created_at">
                      {{
                        t('CONTACTS_LAYOUT.DUPLICATES.CREATED_AT', {
                          date: formatCreatedAt(contact.created_at),
                        })
                      }}
                    </span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </main>

    <Dialog
      ref="confirmDialogRef"
      type="alert"
      :title="t('CONTACTS_LAYOUT.DUPLICATES.CONFIRM.TITLE')"
      :description="t('CONTACTS_LAYOUT.DUPLICATES.CONFIRM.DESCRIPTION')"
      :confirm-button-label="t('CONTACTS_LAYOUT.DUPLICATES.CONFIRM.CONFIRM')"
      @confirm="mergeGroup"
    />
  </div>
</template>
